import { useNavigate } from 'react-router-dom';
import { Mic, Pause, Play, Square, X } from 'lucide-react';
import { useSessionRecording } from '@/contexts/SessionRecordingContext';
import { cn } from '@/lib/utils';

function formatDuration(s: number) {
  const m = Math.floor(s / 60).toString().padStart(2, '0');
  const sec = (s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
}

export function FloatingRecordingBar() {
  const navigate = useNavigate();
  const { recordingState, patientName, duration, pauseRecording, resumeRecording, discardRecording } = useSessionRecording();

  if (recordingState === 'idle') return null;

  const isRecording = recordingState === 'recording';
  const isStopping = recordingState === 'stopping';

  return (
    <div
      className={cn(
        'fixed bottom-[56px] lg:bottom-4 left-1/2 -translate-x-1/2 z-50',
        'flex items-center gap-3 px-4 py-2.5 rounded-2xl shadow-lg border',
        'bg-card border-primary/30 text-foreground',
        'animate-in slide-in-from-bottom-4 duration-300',
        isStopping && 'opacity-60 pointer-events-none',
      )}
      style={{ minWidth: '280px' }}
    >
      {/* Pulsing recording dot */}
      <div className="relative flex items-center justify-center w-7 h-7 shrink-0">
        {isRecording && (
          <span className="absolute inline-flex h-5 w-5 rounded-full bg-red-400 opacity-50 animate-ping" />
        )}
        <span className={cn('relative inline-flex rounded-full w-3 h-3', isRecording ? 'bg-red-500' : 'bg-amber-500')} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold truncate">
          {isStopping ? 'Wird gespeichert…' : isRecording ? 'Aufnahme läuft' : 'Aufnahme pausiert'}
        </p>
        <p className="text-[11px] text-muted-foreground truncate">
          {patientName ?? 'Kein Patient'} · {formatDuration(duration)}
        </p>
      </div>

      {/* Controls */}
      {!isStopping && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            title={isRecording ? 'Pause' : 'Fortsetzen'}
            onClick={isRecording ? pauseRecording : resumeRecording}
            className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
          >
            {isRecording ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>

          <button
            title="Zur Aufnahme"
            onClick={() => navigate('/session')}
            className="p-1.5 rounded-lg hover:bg-secondary transition-colors text-primary hover:text-primary/80"
          >
            <Mic className="w-4 h-4" />
          </button>

          <button
            title="Aufnahme verwerfen"
            onClick={() => {
              if (confirm('Aufnahme verwerfen? Die Aufnahme und das Transkript gehen verloren.')) {
                discardRecording();
              }
            }}
            className="p-1.5 rounded-lg hover:bg-destructive/10 transition-colors text-muted-foreground hover:text-destructive"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {isStopping && (
        <Square className="w-4 h-4 text-muted-foreground animate-pulse shrink-0" />
      )}
    </div>
  );
}
